import dayjs from '~/timeInit';

import { APP_EXCLUSIVE_TIMEZONE } from '~/consts/appExclusive';

import type { ScheduleRecord } from '~/services/OffersService';

export type AppExclusiveStatus = 'scheduled' | 'live' | 'ended';

export interface CalendarDay {
  date: string;
  dayOfMonth: number;
  inMonth: boolean;
  isWeekend: boolean;
}

const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_FORMAT = 'YYYY-MM';
const DAYS_PER_WEEK = 7;

// Monday-first weekday index (0 = Monday ... 6 = Sunday).
function mondayIndex(date: dayjs.Dayjs): number {
  return (date.day() + 6) % DAYS_PER_WEEK;
}

function parseMonth(month: string): dayjs.Dayjs {
  return dayjs.utc(`${month}-01`, DATE_FORMAT);
}

export function currentMonth(now: dayjs.Dayjs = dayjs()): string {
  return now.tz(APP_EXCLUSIVE_TIMEZONE).format(MONTH_FORMAT);
}

export function todayDate(now: dayjs.Dayjs = dayjs()): string {
  return now.tz(APP_EXCLUSIVE_TIMEZONE).format(DATE_FORMAT);
}

export function shiftMonth(month: string, delta: number): string {
  return parseMonth(month).add(delta, 'month').format(MONTH_FORMAT);
}

export function formatMonthLabel(month: string): string {
  return parseMonth(month).format('MMMM YYYY');
}

/** Whole weeks (Monday to Sunday) covering the month, including leading and trailing days. */
export function buildCalendarDays(month: string): Array<CalendarDay> {
  const first = parseMonth(month);
  const gridStart = first.subtract(mondayIndex(first), 'day');
  const totalDays = Math.ceil((mondayIndex(first) + first.daysInMonth()) / DAYS_PER_WEEK) * DAYS_PER_WEEK;

  return Array.from({ length: totalDays }, (_, index) => {
    const date = gridStart.add(index, 'day');
    return {
      date: date.format(DATE_FORMAT),
      dayOfMonth: date.date(),
      inMonth: date.format(MONTH_FORMAT) === month,
      isWeekend: mondayIndex(date) >= 5,
    };
  });
}

/**
 * svc-offer only returns rows with `start >= start` and `end < end`, so the range is padded by a
 * day either side of the visible grid to keep slots on the grid edges.
 */
export function getCalendarQueryRange(days: Array<CalendarDay>): { start: string; end: string } {
  const first = dayjs.tz(days[0].date, APP_EXCLUSIVE_TIMEZONE).subtract(1, 'day');
  const last = dayjs.tz(days[days.length - 1].date, APP_EXCLUSIVE_TIMEZONE).add(2, 'day');
  return { start: first.toISOString(), end: last.toISOString() };
}

/** Calendar day a slot belongs to: its start date in Sydney time (slots start 6am Sydney). */
export function getScheduleDate(schedule: Pick<ScheduleRecord, 'start'>): string {
  return dayjs(schedule.start).tz(APP_EXCLUSIVE_TIMEZONE).format(DATE_FORMAT);
}

export function getScheduleStatus(
  schedule: Pick<ScheduleRecord, 'start' | 'end'>,
  now: dayjs.Dayjs = dayjs(),
): AppExclusiveStatus {
  if (now.isBefore(dayjs(schedule.start))) {
    return 'scheduled';
  }
  if (now.isBefore(dayjs(schedule.end))) {
    return 'live';
  }
  return 'ended';
}

export function groupSchedulesByDate(schedules: Array<ScheduleRecord>): Map<string, Array<ScheduleRecord>> {
  const byDate = new Map<string, Array<ScheduleRecord>>();
  [...schedules]
    .sort((a, b) => a.start.localeCompare(b.start) || a.id - b.id)
    .forEach((schedule) => {
      const date = getScheduleDate(schedule);
      byDate.set(date, [...(byDate.get(date) ?? []), schedule]);
    });
  return byDate;
}

export function formatSlotTime(iso: string): string {
  return dayjs(iso).tz(APP_EXCLUSIVE_TIMEZONE).format('D MMM, h:mma');
}

export function formatSlotTimeOfDay(iso: string): string {
  return dayjs(iso).tz(APP_EXCLUSIVE_TIMEZONE).format('h:mma');
}

// Defensive: other offer shapes (e.g. App.ContentOffer) model location as a plain string.
type OfferLocation = App.Offer['location'] | string;

/**
 * Short location label for an event card. Accommodation offers carry a structured location
 * (`heading`/`subheading`/`description`); tolerate a plain string too.
 */
export function getOfferLocationLabel(location: OfferLocation | undefined): string | undefined {
  if (!location) {
    return undefined;
  }
  if (typeof location === 'string') {
    return location || undefined;
  }
  return [location.heading, location.subheading].filter(Boolean).join(', ') || undefined;
}
